import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  PlusIcon, PencilIcon, TrashIcon, XMarkIcon, AcademicCapIcon,
} from '@heroicons/react/24/outline';

const COLOR_OPTIONS = [
  { val: 'blue', label: 'Blue (Infants)', class: 'bg-blue-200' },
  { val: 'orange', label: 'Orange (Babies)', class: 'bg-orange-200' },
  { val: 'yellow', label: 'Yellow (Toddlers)', class: 'bg-yellow-200' },
  { val: 'green', label: 'Green (PreK)', class: 'bg-green-200' },
  { val: 'pink', label: 'Pink (Schools)', class: 'bg-pink-200' },
  { val: 'cyan', label: 'Cyan (Training)', class: 'bg-cyan-200' },
  { val: 'gray', label: 'Gray (Admin / Floats)', class: 'bg-gray-200' },
];

export default function ClassroomsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/classrooms');
      setRows(data);
    } catch { toast.error('Failed to load classrooms'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (c = null) => { setEditing(c); setShowForm(true); };

  const remove = async (id) => {
    if (!window.confirm('Archive this classroom? Existing schedule rows that reference it will keep working.')) return;
    try {
      await api.delete(`/classrooms/${id}`);
      toast.success('Archived');
      load();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="animate-slide-up">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AcademicCapIcon className="w-6 h-6" /> Classrooms & Ratios
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Each classroom has a max kids-per-staff ratio and a max capacity. The AI scheduler uses these to make sure each room is properly staffed.
          </p>
        </div>
        <button onClick={() => open()} className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 inline-flex items-center gap-1">
          <PlusIcon className="w-4 h-4" /> New Classroom
        </button>
      </header>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase">
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Age range</th>
              <th className="px-4 py-2">Ratio (kids/staff)</th>
              <th className="px-4 py-2">Capacity</th>
              <th className="px-4 py-2">Color</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">{c.name}</td>
                <td className="px-4 py-2 text-gray-600">
                  {c.age_min_months != null && c.age_max_months != null
                    ? `${c.age_min_months}–${c.age_max_months} mo`
                    : '—'}
                </td>
                <td className="px-4 py-2 text-gray-700">1 : {c.max_kids_per_staff}</td>
                <td className="px-4 py-2 text-gray-700">{c.max_capacity || '—'}</td>
                <td className="px-4 py-2">
                  {c.color && (
                    <span className={`inline-block w-4 h-4 rounded ${COLOR_OPTIONS.find(o => o.val === c.color)?.class || 'bg-gray-200'}`} />
                  )}
                  <span className="text-xs text-gray-500 ml-2">{c.color || '—'}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => open(c)} className="text-gray-400 hover:text-orange-500 mr-2"><PencilIcon className="w-4 h-4" /></button>
                  <button onClick={() => remove(c.id)} className="text-gray-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ClassroomForm classroom={editing} onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ClassroomForm({ classroom, onClose, onSaved }) {
  const { register, handleSubmit } = useForm({ defaultValues: classroom || { display_order: 0, max_kids_per_staff: 5 } });
  const [saving, setSaving] = useState(false);

  const submit = async (form) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        display_order: form.display_order ? Number(form.display_order) : 0,
        age_min_months: form.age_min_months !== '' ? Number(form.age_min_months) : null,
        age_max_months: form.age_max_months !== '' ? Number(form.age_max_months) : null,
        max_kids_per_staff: Number(form.max_kids_per_staff),
        max_capacity: form.max_capacity !== '' ? Number(form.max_capacity) : null,
      };
      if (classroom) await api.patch(`/classrooms/${classroom.id}`, payload);
      else await api.post('/classrooms', payload);
      toast.success(classroom ? 'Updated' : 'Created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={classroom ? 'Edit Classroom' : 'New Classroom'} onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <Input label="Name *" {...register('name', { required: true })} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Age min (months)" type="number" {...register('age_min_months')} />
          <Input label="Age max (months)" type="number" {...register('age_max_months')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Max kids per staff *" type="number" {...register('max_kids_per_staff', { required: true })} />
          <Input label="Max capacity" type="number" {...register('max_capacity')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <select {...register('color')} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
              <option value="">—</option>
              {COLOR_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
            </select>
          </div>
          <Input label="Display order" type="number" {...register('display_order')} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea {...register('notes')} rows={2}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function Spinner() {
  return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
}

function Input({ label, ...props }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input {...props} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
